import React, { useState } from "react";
import styled from "@emotion/styled";
import { useDraggable } from "@dnd-kit/core";
import { useGetUIBlocksQuery, UIBlock } from "../../api/uiBlocksApi";
import { UIBlockRenderer } from "./UIBlockRenderer";

const CatalogContainer = styled.div`
  width: 300px;
  height: 100vh;
  background: #f8f9fa;
  border-right: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CatalogHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  background: white;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#FF6B6B" : "#d1d5db")};
  background: ${(props) => (props.active ? "#FF6B6B" : "white")};
  color: ${(props) => (props.active ? "white" : "#374151")};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "#FF6B6B" : "#f9fafb")};
  }
`;

const CatalogContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const BlockGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #ef4444;
  text-align: center;
  font-size: 14px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #6b7280;
  text-align: center;

  h4 {
    margin: 0 0 8px 0;
    color: #374151;
  }
`;

const DraggableBlockCard = styled.div<{ isDragging: boolean }>`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  cursor: grab;
  transition: all 0.2s;
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transform: ${(props) => (props.isDragging ? "rotate(5deg)" : "none")};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    cursor: grabbing;
  }
`;

const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const BlockName = styled.h4`
  margin: 0;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

const PremiumBadge = styled.span`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const BlockDescription = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
`;

const CategoryBadge = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: capitalize;
  margin-top: 8px;
  display: inline-block;
`;

const PopoverContainer = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-width: 400px;
  max-height: 500px;
  overflow: hidden;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.95)'};
  transition: all 0.2s ease;
`;

const PopoverHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8f9fa;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const PopoverContent = styled.div`
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
  transform: scale(0.8);
  transform-origin: top left;
`;

interface DraggableBlockProps {
  block: UIBlock;
  onMouseEnter?: (block: UIBlock, element: HTMLElement) => void;
  onMouseLeave?: () => void;
}

const DraggableBlock: React.FC<DraggableBlockProps> = ({ block, onMouseEnter, onMouseLeave }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `block-${block.id}`,
      data: {
        type: "ui-block",
        block,
      },
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onMouseEnter) {
      onMouseEnter(block, e.currentTarget);
    }
  };

  return (
    <DraggableBlockCard
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      {...listeners}
      {...attributes}
    >
      <BlockHeader>
        <BlockName>{block.name}</BlockName>
        {block.isPremium && <PremiumBadge>Pro</PremiumBadge>}
      </BlockHeader>
      {/* <BlockDescription>{block.description}</BlockDescription> */}
      <CategoryBadge>{block.category}</CategoryBadge>
    </DraggableBlockCard>
  );
};

const categories = [
  { key: "", label: "All" },
  { key: "hero", label: "Hero" },
  { key: "features", label: "Features" },
  { key: "pricing", label: "Pricing" },
  { key: "testimonials", label: "Reviews" },
  { key: "contact", label: "Contact" },
  { key: "footer", label: "Footer" },
  { key: "navigation", label: "Nav" },
];

interface BlockCatalogProps {
  onBlockSelect?: (block: UIBlock) => void;
}

interface HoverState {
  block: UIBlock | null;
  visible: boolean;
  x: number;
  y: number;
}

export const BlockCatalog: React.FC<BlockCatalogProps> = ({
  onBlockSelect,
}) => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [hoverState, setHoverState] = useState<HoverState>({
    block: null,
    visible: false,
    x: 0,
    y: 0
  });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const {
    data: blocks = [],
    isLoading,
    error,
  } = useGetUIBlocksQuery({
    category: activeCategory || undefined,
  });

  const filteredBlocks = blocks.filter(
    (block) => block.name.toLowerCase().includes(search.toLowerCase())
    //block.description.toLowerCase().includes(search.toLowerCase()
  );

  const handleBlockHover = (block: UIBlock, element: HTMLElement) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    const rect = element.getBoundingClientRect();
    const x = rect.right + 10;
    const y = rect.top;
    
    const timeout = setTimeout(() => {
      setHoverState({
        block,
        visible: true,
        x,
        y
      });
    }, 300);
    
    setHoverTimeout(timeout);
  };

  const handleBlockLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    setHoverState(prev => ({
      ...prev,
      visible: false
    }));
  };

  return (
    <CatalogContainer>
      <CatalogHeader>
        <Title>UI Blocks</Title>
        <SearchInput
          type="text"
          placeholder="Search blocks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CategoryTabs>
          {categories.map((category) => (
            <CategoryTab
              key={category.key}
              active={activeCategory === category.key}
              onClick={() => setActiveCategory(category.key)}
            >
              {category.label}
            </CategoryTab>
          ))}
        </CategoryTabs>
      </CatalogHeader>

      <CatalogContent>
        {isLoading && <LoadingState>Loading blocks...</LoadingState>}

        {error && (
          <ErrorState>
            Failed to load UI blocks.
            <br />
            Please try again.
          </ErrorState>
        )}

        {!isLoading && !error && filteredBlocks.length === 0 && (
          <EmptyState>
            <h4>No blocks found</h4>
            <p>
              {search || activeCategory
                ? "Try adjusting your search or category filter"
                : "No UI blocks are available"}
            </p>
          </EmptyState>
        )}

        {!isLoading && !error && filteredBlocks.length > 0 && (
          <BlockGrid>
            {filteredBlocks.map((block) => (
              <DraggableBlock 
                key={block.id} 
                block={block} 
                onMouseEnter={handleBlockHover}
                onMouseLeave={handleBlockLeave}
              />
            ))}
          </BlockGrid>
        )}
      </CatalogContent>
      
      <PopoverContainer 
        visible={hoverState.visible} 
        x={hoverState.x} 
        y={hoverState.y}
      >
        {hoverState.block && (
          <>
            <PopoverHeader>
              {hoverState.block.name} Preview
            </PopoverHeader>
            <PopoverContent>
              <UIBlockRenderer
                block={{
                  id: hoverState.block.id,
                  name: hoverState.block.name,
                  html: hoverState.block.html || '',
                  css: hoverState.block.css || '',
                  js: hoverState.block.js || null,
                  schema: hoverState.block.schema,
                  slots: hoverState.block.slots,
                  isContainer: hoverState.block.isContainer
                }}
                overrides={{}}
                isEditable={false}
              />
            </PopoverContent>
          </>
        )}
      </PopoverContainer>
    </CatalogContainer>
  );
};
